// SegmentIndexStore.ts
// MobX store managing the segment index for one-year replay data.
// Tracks all segments (in IndexedDB and memory), provides lookup/query APIs.

import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import {
    Segment,
    SegmentIndexEntry,
} from './SegmentSchema';

const INDEXEDDB_NAME = 'SmartChartsReplay';
const SEGMENTS_STORE = 'segments';
const METADATA_STORE = 'metadata';
const DB_VERSION = 1;

/**
 * SegmentIndexStore: maintains an in-memory index of all segments,
 * provides query/filter APIs, and manages IndexedDB persistence.
 */
export class SegmentIndexStore {
    // In-memory index: segmentId -> entry
    private _index: Map<string, SegmentIndexEntry> = new Map();

    // Loaded segments: segmentId -> full segment data
    private _loadedSegments: Map<string, Segment> = new Map();

    // IndexedDB connection
    private _db: IDBDatabase | null = null;

    // Observable state for UI/debugging
    totalSegments = 0;
    totalSizeBytes = 0;
    isInitialized = false;

    constructor() {
        makeObservable(this, {
            totalSegments: observable,
            totalSizeBytes: observable,
            isInitialized: observable,
            symbolList: computed,
            initialize: action,
            addSegmentToIndex: action,
            removeSegmentFromIndex: action,
            clearIndex: action,
        });
    }

    /**
     * Initialize: open IndexedDB and load segment index into memory.
     */
    async initialize(): Promise<void> {
        if (this.isInitialized) return;

        try {
            this._db = await this._openDB();
            await this._loadIndexFromDB();
            runInAction(() => {
                this.isInitialized = true;
            });
            console.log('[SegmentIndexStore] Initialized:', {
                totalSegments: this.totalSegments,
                totalSizeBytes: this.totalSizeBytes,
            });
        } catch (error) {
            console.error('[SegmentIndexStore] Failed to initialize:', error);
            throw error;
        }
    }

    /**
     * Get list of all symbols with stored segments.
     */
    get symbolList(): string[] {
        const symbols = new Set<string>();
        for (const entry of this._index.values()) {
            symbols.add(entry.symbol);
        }
        return Array.from(symbols).sort();
    }

    /**
     * Query segments by symbol and time range.
     */
    querySegments(symbol: string, startEpoch: number, endEpoch: number, granularity?: number): SegmentIndexEntry[] {
        console.log('[SegmentIndexStore] querySegments called:', {
            symbol,
            startEpoch,
            endEpoch,
            startDate: new Date(startEpoch),
            endDate: new Date(endEpoch),
            granularity,
            totalIndexSize: this._index.size
        });

        const results: SegmentIndexEntry[] = [];
        let matchedSymbol = 0;
        let matchedTime = 0;
        let matchedGranularity = 0;
        
        for (const entry of this._index.values()) {
            const matchesSymbol = entry.symbol === symbol;
            const matchesTimeRange = entry.endEpoch >= startEpoch && entry.startEpoch <= endEpoch;
            const matchesGranularity = granularity === undefined || entry.granularity === granularity;
            
            if (matchesSymbol) matchedSymbol++;
            if (matchesSymbol && matchesTimeRange) matchedTime++;
            if (matchesSymbol && matchesTimeRange && matchesGranularity) matchedGranularity++;
            
            if (matchesSymbol && matchesTimeRange && matchesGranularity) {
                results.push(entry);
            }
        }
        
        console.log('[SegmentIndexStore] Query results:', {
            totalInIndex: this._index.size,
            matchedSymbol,
            matchedTime,
            matchedGranularity,
            resultCount: results.length
        });

        // Log first and last segment if results exist
        if (results.length > 0) {
            const sorted = results.sort((a, b) => a.startEpoch - b.startEpoch);
            console.log('[SegmentIndexStore] First segment:', {
                id: sorted[0].segmentId,
                start: new Date(sorted[0].startEpoch),
                end: new Date(sorted[0].endEpoch)
            });
            console.log('[SegmentIndexStore] Last segment:', {
                id: sorted[sorted.length - 1].segmentId,
                start: new Date(sorted[sorted.length - 1].startEpoch),
                end: new Date(sorted[sorted.length - 1].endEpoch)
            });
            return sorted;
        }

        // If no results, log sample of what IS in the index for this symbol
        const sampleEntries = Array.from(this._index.values())
            .filter(e => e.symbol === symbol)
            .sort((a, b) => a.startEpoch - b.startEpoch)
            .slice(0, 5);
        
        if (sampleEntries.length > 0) {
            console.log('[SegmentIndexStore] Sample of available segments for', symbol + ':', 
                sampleEntries.map(e => ({
                    id: e.segmentId,
                    start: new Date(e.startEpoch),
                    end: new Date(e.endEpoch),
                    granularity: e.granularity
                }))
            );
        }
        
        // Sort by startEpoch
        return results.sort((a, b) => a.startEpoch - b.startEpoch);
    }

    /**
     * Get all segments for a symbol.
     */
    getSegmentsBySymbol(symbol: string): SegmentIndexEntry[] {
        const results: SegmentIndexEntry[] = [];
        for (const entry of this._index.values()) {
            if (entry.symbol === symbol) {
                results.push(entry);
            }
        }
        return results.sort((a, b) => a.startEpoch - b.startEpoch);
    }

    /**
     * Get segment by ID.
     */
    getSegmentById(segmentId: string): SegmentIndexEntry | undefined {
        return this._index.get(segmentId);
    }

    /**
     * Check if segment is loaded in memory.
     */
    isSegmentLoaded(segmentId: string): boolean {
        return this._loadedSegments.has(segmentId);
    }

    /**
     * Get loaded segment data.
     */
    getLoadedSegment(segmentId: string): Segment | undefined {
        return this._loadedSegments.get(segmentId);
    }

    /**
     * Load segment from IndexedDB into memory.
     */
    async loadSegment(segmentId: string): Promise<Segment | null> {
        const entry = this._index.get(segmentId);
        if (!entry) {
            console.warn('[SegmentIndexStore] Segment not in index:', segmentId);
            return null;
        }

        // Check if already loaded
        if (this._loadedSegments.has(segmentId)) {
            this._updateAccessTime(segmentId);
            return this._loadedSegments.get(segmentId)!;
        }

        try {
            const segment = await this._loadSegmentFromDB(segmentId);
            if (segment) {
                this._loadedSegments.set(segmentId, segment);
                runInAction(() => {
                    entry.isLoaded = true;
                    this._updateAccessTime(segmentId);
                });
                console.log('[SegmentIndexStore] Loaded segment:', segmentId);
            }
            return segment;
        } catch (error) {
            console.error('[SegmentIndexStore] Failed to load segment:', segmentId, error);
            return null;
        }
    }

    /**
     * Unload segment from memory (keep in IndexedDB).
     */
    unloadSegment(segmentId: string): void {
        if (this._loadedSegments.has(segmentId)) {
            this._loadedSegments.delete(segmentId);
            const entry = this._index.get(segmentId);
            if (entry) {
                runInAction(() => {
                    entry.isLoaded = false;
                });
            }
            console.log('[SegmentIndexStore] Unloaded segment:', segmentId);
        }
    }

    /**
     * Add/update segment in index and persist to IndexedDB.
     */
    async addSegmentToIndex(segment: Segment): Promise<void> {
        const { metadata } = segment;
        const entry: SegmentIndexEntry = {
            segmentId: metadata.segmentId,
            symbol: metadata.symbol,
            granularity: metadata.granularity,
            type: metadata.type,
            startEpoch: metadata.startEpoch,
            endEpoch: metadata.endEpoch,
            count: metadata.count,
            sizeBytes: metadata.sizeBytes,
            compression: metadata.compression,
            createdAt: metadata.createdAt,
            isLoaded: true,
            lastAccessedAt: Date.now(),
        };

        this._index.set(entry.segmentId, entry);
        this._loadedSegments.set(entry.segmentId, segment);

        runInAction(() => {
            this.totalSegments = this._index.size;
            this.totalSizeBytes = Array.from(this._index.values()).reduce((sum, e) => sum + e.sizeBytes, 0);
        });

        // Persist to IndexedDB
        await this._saveSegmentToDB(segment);
        await this._saveMetadataToDB(entry);

        console.log('[SegmentIndexStore] Added segment:', entry.segmentId);
    }

    /**
     * Remove segment from index and IndexedDB.
     */
    async removeSegmentFromIndex(segmentId: string): Promise<void> {
        const entry = this._index.get(segmentId);
        if (!entry) return;

        this._index.delete(segmentId);
        this._loadedSegments.delete(segmentId);

        runInAction(() => {
            this.totalSegments = this._index.size;
            this.totalSizeBytes = Array.from(this._index.values()).reduce((sum, e) => sum + e.sizeBytes, 0);
        });

        // Remove from IndexedDB
        await this._deleteSegmentFromDB(segmentId);
        await this._deleteMetadataFromDB(segmentId);

        console.log('[SegmentIndexStore] Removed segment:', segmentId);
    }

    /**
     * Clear all segments (for testing or reset).
     */
    async clearIndex(): Promise<void> {
        this._index.clear();
        this._loadedSegments.clear();

        runInAction(() => {
            this.totalSegments = 0;
            this.totalSizeBytes = 0;
        });

        await this._clearDB();
        console.log('[SegmentIndexStore] Cleared all segments');
    }

    /**
     * Get total storage used (bytes).
     */
    getTotalStorageBytes(): number {
        return this.totalSizeBytes;
    }

    /**
     * Get segments sorted by last accessed (LRU candidates).
     */
    getLRUCandidates(limit: number): SegmentIndexEntry[] {
        const loaded = Array.from(this._index.values()).filter(e => e.isLoaded);
        loaded.sort((a, b) => (a.lastAccessedAt || 0) - (b.lastAccessedAt || 0));
        return loaded.slice(0, limit);
    }

    // Private methods

    private _updateAccessTime(segmentId: string): void {
        const entry = this._index.get(segmentId);
        if (entry) {
            entry.lastAccessedAt = Date.now();
        }
    }

    private async _openDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(INDEXEDDB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Create segments store
                if (!db.objectStoreNames.contains(SEGMENTS_STORE)) {
                    db.createObjectStore(SEGMENTS_STORE, { keyPath: 'metadata.segmentId' });
                }

                // Create metadata store
                if (!db.objectStoreNames.contains(METADATA_STORE)) {
                    const metaStore = db.createObjectStore(METADATA_STORE, { keyPath: 'segmentId' });
                    metaStore.createIndex('symbol', 'symbol', { unique: false });
                    metaStore.createIndex('startEpoch', 'startEpoch', { unique: false });
                }
            };
        });
    }

    private async _loadIndexFromDB(): Promise<void> {
        if (!this._db) return;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([METADATA_STORE], 'readonly');
            const store = transaction.objectStore(METADATA_STORE);
            const request = store.getAll();

            request.onsuccess = () => {
                const entries = request.result as SegmentIndexEntry[];
                for (const entry of entries) {
                    entry.isLoaded = false; // Start with all unloaded
                    this._index.set(entry.segmentId, entry);
                }
                runInAction(() => {
                    this.totalSegments = this._index.size;
                    this.totalSizeBytes = entries.reduce((sum, e) => sum + e.sizeBytes, 0);
                });
                resolve();
            };

            request.onerror = () => reject(request.error);
        });
    }

    private async _loadSegmentFromDB(segmentId: string): Promise<Segment | null> {
        if (!this._db) return null;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([SEGMENTS_STORE], 'readonly');
            const store = transaction.objectStore(SEGMENTS_STORE);
            const request = store.get(segmentId);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    private async _saveSegmentToDB(segment: Segment): Promise<void> {
        if (!this._db) return;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([SEGMENTS_STORE], 'readwrite');
            const store = transaction.objectStore(SEGMENTS_STORE);
            const request = store.put(segment);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    private async _saveMetadataToDB(entry: SegmentIndexEntry): Promise<void> {
        if (!this._db) return;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([METADATA_STORE], 'readwrite');
            const store = transaction.objectStore(METADATA_STORE);
            const request = store.put(entry);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    private async _deleteSegmentFromDB(segmentId: string): Promise<void> {
        if (!this._db) return;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([SEGMENTS_STORE], 'readwrite');
            const store = transaction.objectStore(SEGMENTS_STORE);
            const request = store.delete(segmentId);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    private async _deleteMetadataFromDB(segmentId: string): Promise<void> {
        if (!this._db) return;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([METADATA_STORE], 'readwrite');
            const store = transaction.objectStore(METADATA_STORE);
            const request = store.delete(segmentId);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    private async _clearDB(): Promise<void> {
        if (!this._db) return;

        return new Promise((resolve, reject) => {
            const transaction = this._db!.transaction([SEGMENTS_STORE, METADATA_STORE], 'readwrite');
            
            const segmentStore = transaction.objectStore(SEGMENTS_STORE);
            const metadataStore = transaction.objectStore(METADATA_STORE);
            
            const clearSegments = segmentStore.clear();
            const clearMetadata = metadataStore.clear();

            let completed = 0;
            const checkComplete = () => {
                completed++;
                if (completed === 2) resolve();
            };

            clearSegments.onsuccess = checkComplete;
            clearMetadata.onsuccess = checkComplete;

            clearSegments.onerror = () => reject(clearSegments.error);
            clearMetadata.onerror = () => reject(clearMetadata.error);
        });
    }
}

// Singleton instance
export const segmentIndexStore = new SegmentIndexStore();
